import { Component, Vue } from 'nuxt-property-decorator'
import mapboxgl from 'mapbox-gl'
import { Point } from 'geojson'
import { PublicProject } from '~/models/project'
import MapCardAmenity from '~/components/consumer_map/MapCardAmenity.vue'
import RichTooltipPoi from '~/components/shared/poi/RichTooltipPOI.vue'

import hexRGB from '~/utils/hexRGB'

@Component
export default class Amenities extends Vue {
  map: mapboxgl.Map
  dotColor: string
  amenities: Array<any>
  project: PublicProject
  poiLayerName: string
  popup: any

  setUpAmenities(
    map: any,
    dotColor: string,
    amenities: Array<any>,
    project: PublicProject
  ): void {
    this.map = map
    this.dotColor = dotColor
    this.amenities = amenities
    this.project = project
    this.initAmenities()

    this.map.on('click', this.poiLayerName, this.setAmenityTooltipOnMap)

    this.map.on('mouseenter', this.poiLayerName, () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })

    this.map.on('mouseleave', this.poiLayerName, () => {
      this.map.getCanvas().style.cursor = ''
    })
  }

  setAmenitiesVisualAid(
    id: string,
    image: string,
    color: string = this.dotColor,
    pulsing: boolean = true
  ): void {
    if (this.map && !this.map.hasImage(id)) {
      if (image) {
        this.map.loadImage(image, (error, image) => {
          if (error) throw error

          // Add the image to the map style.
          this.map.addImage(id, image)
        })
        return
      }
      this.map.addImage(id, this.pulsingDot(this.map, color, pulsing), {
        pixelRatio: 2,
      })
    }
  }

  mapAmenitiesData(amenities: any): Array<any> {
    const mappedData: any[] = []
    amenities.forEach((elem: any) => {
      if (!elem.legacy) {
        const {
          name,
          address,
          description,
          subTitle,
          images,
          type,
          amenities,
          categoryNames,
          heroImages,
          markerIsPulsing,
          markerUrls,
          markerColor,
          videos,
        } = elem
        const virtualTourUrl = elem['3dTourUrl']
        const { geoCoordinates, addressStringTemp } = address
        const { longitude, latitude } = geoCoordinates
        const properties: any = {
          name,
          address: addressStringTemp,
          snippet: subTitle,
          description,
          images,
          type,
          heroImage: heroImages,
          amenities,
          categories: categoryNames,
          longitude,
          latitude,
          virtualTourUrl,
          markerIsPulsing,
          markerUrls: markerUrls[0] || undefined,
          markerColor,
          id: name ? name.replaceAll(' ', '-') : 'no-name',
          videos,
        }

        this.setAmenitiesVisualAid(
          properties.id,
          properties.markerUrls,
          properties.markerColor,
          properties.markerIsPulsing
        )

        if (type === 'Rich') {
          const { buttonLink, buttonText } = elem
          properties.buttonLink = buttonLink
          properties.buttonText = buttonText
        }
        const obj = {
          type: 'Feature',
          properties,
          geometry: {
            type: 'Point',
            coordinates: [longitude, latitude],
          },
        }
        mappedData.push(obj)
      } else {
        const obj = {
          type: 'Feature',
          properties: {
            description: elem.snippet,
            name: elem.name,
            photos: elem.photos,
            legacy: true,
            id: elem && elem.name ? elem.name.replaceAll(' ', '-') : 'no-name',
          },
          geometry: {
            type: 'Point',
            coordinates: [elem.longitude, elem.latitude],
          },
        }
        const { properties } = obj
        this.setAmenitiesVisualAid(properties.id, null, this.dotColor, true)
        mappedData.push(obj)
      }
    })
    return mappedData
  }

  updateAmenities(): void {
    const mappedData: any = this.mapAmenitiesData(this.amenities)
    const source: any = this.map?.getSource('amenities')
    if (source) {
      source?.setData({
        type: 'FeatureCollection',
        features: mappedData,
      })
    }
  }

  initAmenities(): void {
    const mappedData: any = this.mapAmenitiesData(this.amenities)
    if (!this.map.getSource('amenities'))
      this.map.addSource('amenities', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: mappedData,
        },
        cluster: true,
        clusterMaxZoom: 14,
      })

    // creating a unique layer
    const slug = this.$route?.params?.slug || 'pois'
    this.poiLayerName = `${slug}-amenities`
    if (this.map.getLayer(this.poiLayerName)) {
      this.poiLayerName += Date.now()
    }

    if (!this.map.getLayer(this.poiLayerName))
      this.map.addLayer({
        id: this.poiLayerName,
        type: 'symbol',
        source: 'amenities',
        layout: {
          'icon-image': ['get', 'id'],
          'icon-size': ['step', ['get', 'point_count'], 1, 2, 2],
          'icon-allow-overlap': true,
        },
      })

    if (!this.map.getLayer('clusters'))
      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'amenities',
        filter: ['has', 'point_count'],
        paint: {
          // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          // with three steps to implement three types of circles:
          //   * small size circle, 15px  when point count is less than 100
          //   * medium size circle, 25px  when point count is between 100 and 750
          //   * big size circle, 35px  when point count is greater than or equal to 750
          'circle-color': [
            'step',
            ['get', 'point_count'],
            this.dotColor,
            100,
            this.dotColor,
            750,
            this.dotColor,
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            15,
            100,
            25,
            750,
            35,
          ],
        },
      })
    if (!this.map.getLayer('cluster-count'))
      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'amenities',
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      })
  }

  pulsingDot(map: any, fillColor: any, pulsing: boolean = true): any {
    const size = 100
    const pulse = pulsing
    return {
      width: size,
      height: size,
      fillStyle: fillColor?.trim() || '#000000',
      pulsingFillStyle: fillColor?.trim() || '#646464',
      data: new Uint8Array(size * size * 4),

      // When the layer is added to the map,
      // get the rendering context for the map canvas.
      onAdd() {
        const canvas = document.createElement('canvas')
        canvas.width = this.width
        canvas.height = this.height
        this.context = canvas.getContext('2d')
      },

      // Call once before every frame where the icon will be used.
      render() {
        const radius = (size / 2) * 0.3
        let t = 1

        if (pulse) {
          const duration = 1500
          t = (performance.now() % duration) / duration
        }
        const outerRadius = pulse ? (size / 2) * 0.7 * t + radius : radius
        // Draw the outer circle.
        if (pulse) {
          this.context.clearRect(0, 0, this.width, this.height)
        }
        this.context.beginPath()
        this.context.arc(
          this.width / 2,
          this.height / 2,
          outerRadius,
          0,
          Math.PI * 2
        )

        if (pulse) {
          const RGB = hexRGB(this.pulsingFillStyle)
          this.context.fillStyle = `rgba(${RGB[0]}, ${RGB[1]}, ${RGB[2]}, ${
            1 - t
          })`
          this.context.fill()
        }

        // Draw the inner circle.
        this.context.beginPath()
        this.context.arc(
          this.width / 2,
          this.height / 2,
          radius,
          0,
          Math.PI * 2
        )
        this.context.fillStyle = this.fillStyle
        this.context.strokeStyle = 'white'
        this.context.lineWidth = pulse ? 2 : 2 + 4 * (1 - t)
        this.context.fill()
        this.context.stroke()

        // Update this image's data with data from the canvas.
        this.data = this.context.getImageData(
          0,
          0,
          this.width,
          this.height
        ).data

        // Continuously repaint the map, resulting
        // in the smooth animation of the dot.
        map.triggerRepaint()

        // Return `true` to let the map know that the image was updated.
        return true
      },
    }
  }

  setAmenityTooltipOnMap(e: any): void {
    const isMobile = window.innerWidth < 1024
    if (this.amenities?.length && e?.features?.length) {
      if (isMobile) {
        const { name, snippet, photos, images, legacy } =
          e.features[0].properties
        const amenity: { name: string; snippet: string; photos: string[] } = {
          name,
          snippet,
          photos: [],
        }
        if (!photos) {
          amenity.photos = !legacy
            ? JSON.parse(images).map((img: any) => img?.url)
            : JSON.parse(photos).map((photo: any) => photo?.source)
        }

        this.$emit('setAmenity', amenity)
      } else {
        this.map.getCanvas().style.cursor = 'pointer'
        const coordinates: [number, number] = [
          (e.features[0].geometry as Point).coordinates[0],
          (e.features[0].geometry as Point).coordinates[1],
        ]

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
        }

        const properties = e.features[0].properties
        if (this.popup) {
          this.popup.remove()
          this.popup = null
        }

        if (!properties.legacy) {
          const { tooltip, popupOptions } = this.addTooltip(properties)
          this.popup = this.createPopup(popupOptions, coordinates, tooltip)
          this.popup.isRichPopup = true
        } else {
          this.popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: true,
          })

          this.popup.setLngLat(coordinates)
          this.popup.setHTML(
            '<div id="vue-popup-amenity-content" style="background-color: blue" class="invisible md:visible"></div>'
          )
          this.popup.addTo(this.map)

          const tooltip = new MapCardAmenity({
            parent: new Vue({
              store: this.$store,
            }),
            propsData: {
              name: e.features[0].properties.name,
              description: e.features[0].properties.description,
              photos: JSON.parse(e.features[0].properties.photos),
            },
          })
          tooltip.$mount('#vue-popup-amenity-content')
        }
      }
    }
  }

  createPopup(
    popupOptions: any,
    coordinates: any,
    tooltip: any,
    existingPopup?: any
  ) {
    const isTooltipAVueComponent = typeof tooltip !== 'string'
    if (tooltip) {
      if (existingPopup) {
        existingPopup.remove()
      }
    }
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: true,
      ...popupOptions,
    })
    popup.on('close', () => {
      this.popup = null
    })
    popup.setLngLat(coordinates)

    // Mount Vue component inside the new popup
    if (isTooltipAVueComponent) {
      popup.setHTML(
        `<div id="vue-popup-amenity-content" class="invisible md:visible"></div>`
      )
      tooltip.$destroy()
      popup.setDOMContent(tooltip.$mount().$el)
      // @ts-ignore
      popup.addTo(this.map)
    } else {
      popup.setHTML(
        `<div id="vue-popup-amenity-content" class="invisible md:visible">${tooltip}</div>`
      )
      // @ts-ignore
      popup.addTo(this.map)
    }
    return popup
  }

  addTooltip(properties: any): any {
    const { name, address, thumbnail, type, snippet } = properties

    let { images, heroImage, categories, amenities, videos } = properties

    categories = categories ? JSON.parse(categories) : []
    images = images ? JSON.parse(images) : undefined
    heroImage = heroImage ? JSON.parse(heroImage) : undefined
    amenities = amenities ? JSON.parse(amenities) : []
    videos = videos ? JSON.parse(videos) : []

    properties = {
      ...properties,
      images,
      heroImage,
      categories,
      amenities,
      videos,
    }

    const thumbnailUrl = heroImage?.length
      ? heroImage[0]?.thumbnails?.large?.url
      : images?.length
      ? images[0]?.thumbnails?.large?.url
      : thumbnail

    const subTitleComponent = this.getSubtitleComponent(
      type,
      snippet,
      address,
      amenities
    )

    let html = this.getBasicTooltipHTML(thumbnailUrl, name, subTitleComponent)
    const popupOptions = {
      className: 'tooltip',
      maxWidth: '100%',
      closeButton: false,
      closeOnMove: true,
    }

    if (type === 'Basic') {
      html = this.getSuperBasicTooltipHTML(name, snippet)
    }

    if (properties.type === 'Rich' || properties.type === 'Hero') {
      const component = this.getRichTooltipHTML({
        ...properties,
        categories,
        thumbnail: thumbnailUrl,
      })
      popupOptions.className = 'tooltip-rich'
      popupOptions.closeButton = false
      popupOptions.closeOnMove = false

      return { tooltip: component, popupOptions }
    }

    // if (properties.type === 'Animated' || properties.type === '3D') {
    //   return // this POI type does not have tooltip
    // }
    return { tooltip: html, popupOptions }
  }

  getSubtitleComponent(
    type: string,
    description: string,
    address: string,
    amenities: any
  ) {
    if (type === 'Model') {
      return `
        <a class="
          mt-2
          py-1
          px-2
          flex
          items-center
          text-sm text-gray-400
          border-gray-300 border
          rounded-md
          bg-white
          space-x-1
          whitespace-nowrap
          hover:bg-gray-200
        "
          href="https://www.google.com/maps/dir//${address}/"
          target="_blank"rel="noreferrer noopener"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <span class="font-medium">Get Directions</span>
        </a>`
    }

    if (type === 'Amenity') {
      return `
          <div class="mt-2 flex flex-wrap gap-3">
            ${amenities
              ?.map((amenity: any) =>
                amenity.icon
                  ? `<img src="${amenity.icon?.[0]?.url}" class="w-4 h-4" />`
                  : ''
              )
              .join('')}
          </div>`
    }

    return `<span class="text-gray-400">${description}</span>`
  }

  getSuperBasicTooltipHTML(name: string, description: string) {
    return `<div class="w-64">
                    <p class="text-lg font-medium text-brand-secondary font-brand-secondary">${name}</p>
                    ${
                      description
                        ? `<p class="text-gray-400">${description}</p>`
                        : ''
                    }
                  </div>`
  }

  getBasicTooltipHTML(thumbnail: string, name: string, subTitleComponent: any) {
    return `<div class="w-full flex flex-col space-y-2">
                <img class="w-64 h-32 z-0" src="${thumbnail}" alt="poi logo" />
                <div class="w-64 p-2.5 pt-0 flex flex-col">
                  <span class="text-lg font-medium text-brand-secondary font-brand-secondary">${name}</span>
                  ${subTitleComponent}
                </div>
              </div>`
  }

  getRichTooltipHTML(data: any) {
    return new RichTooltipPoi({
      parent: new Vue({
        store: this.$store,
      }),
      propsData: {
        data,
      },
    })
  }
}
